import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { InlineLoader } from '../ui/page-loader';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  CURRENCY_OPTIONS,
  CURRENCY_SYMBOLS,
  TRIAL_PERIOD_DAYS,
  fetchSubscriptionPlans,
  formatDurationLabel,
  getCurrencySymbol,
} from '../../lib/pricing';

const PENDING_TRIAL_KEY = 'med900_pending_trial';

function readPendingTrial() {
  try {
    const raw = sessionStorage.getItem(PENDING_TRIAL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function FreeTrialOption({
  currency: controlledCurrency,
  className = '',
}) {
  const { api, user, isAuthenticated, hasActiveSubscription } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [internalCurrency, setInternalCurrency] = useState(controlledCurrency || 'AUD');
  const currency = controlledCurrency ?? internalCurrency;

  const trialDays = plans[0]?.trial_period_days || TRIAL_PERIOD_DAYS;
  const hideOption = Boolean(user?.trial_used) || Boolean(hasActiveSubscription);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    async function loadPlans() {
      setLoading(true);
      try {
        const data = await fetchSubscriptionPlans({ api, currency });
        if (!cancelled && Array.isArray(data) && data.length) {
          setPlans(data);
          setSelectedPlanId((current) => current || data.find((p) => p.highlight)?.id || data[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch trial plans:', error);
        toast.error('Could not load plans. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPlans();
    return () => {
      cancelled = true;
    };
  }, [api, currency, open]);

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
  const chargeDate = new Date();
  chargeDate.setDate(chargeDate.getDate() + trialDays);

  useEffect(() => {
    const pending = readPendingTrial();
    const fromQuery = searchParams.get('trial') === '1';
    if (!pending && !fromQuery) return;
    // Don't auto-open on the public plans page — only resume after signup/login.
    if (!fromQuery && location.pathname === '/pricing') return;

    if (pending?.planId) setSelectedPlanId(pending.planId);
    if (pending?.currency && !controlledCurrency) setInternalCurrency(pending.currency);
    if (searchParams.get('plan')) setSelectedPlanId(searchParams.get('plan'));
    setOpen(true);
  }, [searchParams, controlledCurrency, location.pathname]);

  const handleStartTrial = async () => {
    if (!selectedPlan) {
      toast.error('Choose a plan for your free trial first.');
      return;
    }

    const pending = { planId: selectedPlan.id, currency };
    sessionStorage.setItem(PENDING_TRIAL_KEY, JSON.stringify(pending));

    if (!isAuthenticated) {
      navigate(`/signup?redirect=${encodeURIComponent('/billing?trial=1')}`);
      return;
    }

    setProcessing(true);
    try {
      const response = await api.post('/subscriptions/checkout', {
        plan_id: selectedPlan.id,
        currency,
        origin_url: window.location.origin,
        with_trial: true,
      });
      sessionStorage.removeItem(PENDING_TRIAL_KEY);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Trial checkout failed:', error);
      toast.error(error.response?.data?.detail || 'Could not start your free trial');
      setProcessing(false);
    }
  };

  if (hideOption) return null;

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2 decoration-border"
        data-testid="start-free-trial-link"
      >
        Or start a {trialDays}-day free trial
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="free-trial-dialog">
          <DialogHeader>
            <DialogTitle>Start a {trialDays}-day free trial</DialogTitle>
            <DialogDescription>
              Pick the plan you want after the trial. Stripe will collect your card or bank details now and
              automatically charge {selectedPlan ? `${getCurrencySymbol(currency)}${Number(selectedPlan.price).toFixed(2)} ${currency}` : 'that plan'}{' '}
              on {chargeDate.toLocaleDateString()} unless you cancel.
            </DialogDescription>
          </DialogHeader>

          {!controlledCurrency && (
            <div className="flex flex-wrap gap-1.5">
              {CURRENCY_OPTIONS.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setInternalCurrency(code)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                    currency === code
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:border-primary'
                  }`}
                >
                  {CURRENCY_SYMBOLS[code]} {code}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <InlineLoader message="Loading plans" />
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto" role="radiogroup" aria-label="Trial plan">
              {plans.map((plan) => {
                const checked = plan.id === selectedPlanId;
                return (
                  <label
                    key={plan.id}
                    className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 cursor-pointer ${
                      checked ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                    }`}
                    data-testid={`trial-plan-${plan.id}`}
                  >
                    <span className="flex items-center gap-2.5 min-w-0">
                      <input
                        type="radio"
                        name="trial-plan"
                        value={plan.id}
                        checked={checked}
                        onChange={() => setSelectedPlanId(plan.id)}
                        className="accent-primary"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">{plan.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {formatDurationLabel(plan.duration_days)}
                        </span>
                      </span>
                    </span>
                    <span className="text-sm font-semibold whitespace-nowrap">
                      {getCurrencySymbol(currency)}{Number(plan.price).toFixed(2)}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            You won&apos;t be charged today. Cancel anytime before the trial ends from Settings.
          </p>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleStartTrial} disabled={processing || loading || !selectedPlan}>
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Redirecting…
                </>
              ) : (
                'Continue to payment details'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
